import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { clientAPI, campaignsAPI, dialerAPI, dncAPI } from '../../services/api';
import {
  Upload, FileText, Phone, ArrowLeft, ArrowRight, Check, X,
  Loader2, AlertCircle, FileSpreadsheet, File, ChevronDown,
  Ban, CheckCircle, Download, Shield, Clock, Globe
} from 'lucide-react';

const CreateCampaignPage = () => {
  const navigate = useNavigate();
  const { clientUser } = useAuth();
  
  // Wizard steps
  const [step, setStep] = useState(1);
  
  // Step 1: File upload
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState('');
  
  // Step 2: Column mapping
  const [useAutoExtract, setUseAutoExtract] = useState(true);
  const [phoneColumn, setPhoneColumn] = useState('');
  const [nameColumn, setNameColumn] = useState('');
  const [emailColumn, setEmailColumn] = useState('');
  const [extractedContacts, setExtractedContacts] = useState([]);
  const [extracting, setExtracting] = useState(false);
  
  // Step 3: Campaign settings
  const [agents, setAgents] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [campaignData, setCampaignData] = useState({
    name: '',
    description: '',
    agent_id: '',
    from_number: '',
    delay_between_calls: 30,
    max_concurrent_calls: 1,
    max_retries: 2,
    save_recordings: true,
    scheduled_at: null,
    start_mode: 'now', // 'now', 'scheduled', 'recurring'
    // Recurring schedule options
    recurrence_type: null, // 'daily', 'weekly', 'monthly'
    recurrence_days: [],
    recurrence_time: '09:00',
    recurrence_end_date: null,
    // Calling hours (timezone-aware)
    calling_hours: {
      enabled: false,
      start_time: '09:00',
      end_time: '20:00',
      days_of_week: [0, 1, 2, 3, 4], // Mon-Fri
      default_timezone: 'America/New_York'
    }
  });
  
  const [creating, setCreating] = useState(false);

  // Step 2.5: DNC Scrub results
  const [scrubbing, setScrubbing] = useState(false);
  const [scrubResult, setScrubResult] = useState(null);
  const [showBlockedList, setShowBlockedList] = useState(false);

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  // Timezone options
  const timezones = [
    { id: 'America/New_York', name: 'Eastern Time (ET)' },
    { id: 'America/Chicago', name: 'Central Time (CT)' },
    { id: 'America/Denver', name: 'Mountain Time (MT)' },
    { id: 'America/Phoenix', name: 'Arizona (no DST)' },
    { id: 'America/Los_Angeles', name: 'Pacific Time (PT)' },
    { id: 'America/Anchorage', name: 'Alaska Time (AKT)' },
    { id: 'Pacific/Honolulu', name: 'Hawaii Time (HST)' },
  ];

  // Fetch agents and phone numbers
  useEffect(() => {
    const fetchData = async () => {
      if (!clientUser?.id) return;
      try {
        const [agentsRes, numbersRes] = await Promise.all([
          clientAPI.getAgents(clientUser?.id),
          dialerAPI.getPhoneNumbers(clientUser?.id)
        ]);
        
        if (agentsRes.data.agents) {
          setAgents(agentsRes.data.agents);
          if (agentsRes.data.agents.length > 0) {
            setCampaignData(prev => ({ ...prev, agent_id: agentsRes.data.agents[0].id }));
          }
        }
        
        if (numbersRes.data.phone_numbers) {
          setPhoneNumbers(numbersRes.data.phone_numbers);
          if (numbersRes.data.phone_numbers.length > 0) {
            setCampaignData(prev => ({ ...prev, from_number: numbersRes.data.phone_numbers[0].number }));
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };
    
    fetchData();
  }, [clientUser?.id]);

  // Handle file upload
  const handleFileUpload = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setUploading(true);
    setUploadError('');
    setUploadResult(null);
    setScrubResult(null);  // Reset scrub result when new file uploaded
    
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      const response = await campaignsAPI.uploadFile(clientUser?.id, formData);
      
      if (response.data.success) {
        setUploadResult(response.data);
        if (response.data.detected_phone_columns?.length > 0) {
          setPhoneColumn(response.data.detected_phone_columns[0]);
        }
        // Auto-set extracted contacts
        if (response.data.auto_extracted_numbers?.length > 0) {
          setExtractedContacts(response.data.auto_extracted_numbers.map(n => ({ phone: n })));
        }
      }
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  // Handle drag and drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const fakeEvent = { target: { files: [droppedFile] } };
      handleFileUpload(fakeEvent);
    }
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Extract numbers with column mapping
  const handleExtractNumbers = async () => {
    if (!uploadResult?.upload_id) return;
    
    setExtracting(true);
    try {
      const response = await campaignsAPI.extractNumbers(clientUser?.id, uploadResult.upload_id, {
        phone_column: phoneColumn,
        name_column: nameColumn || null,
        email_column: emailColumn || null
      });
      
      if (response.data.success) {
        setExtractedContacts(response.data.contacts);
      }
    } catch (err) {
      console.error('Error extracting numbers:', err);
    } finally {
      setExtracting(false);
    }
  };

  // Scrub contacts against DNC list
  const handleScrubContacts = async () => {
    if (extractedContacts.length === 0) return;
    
    setScrubbing(true);
    setScrubResult(null);
    
    try {
      const response = await dncAPI.scrubContacts(clientUser?.id, extractedContacts);
      
      if (response.data.success) {
        setScrubResult(response.data);
        // Update extractedContacts to only include clean contacts
        setExtractedContacts(response.data.clean_contacts);
      }
    } catch (err) {
      console.error('Error scrubbing contacts:', err);
    } finally {
      setScrubbing(false);
    }
  };

  // Download blocked contacts as CSV
  const downloadBlockedContacts = () => {
    if (!scrubResult?.blocked_contacts?.length) return;
    
    const csvContent = [
      ['Phone', 'Name', 'DNC Reason'].join(','),
      ...scrubResult.blocked_contacts.map(c => 
        [c.phone || '', c.name || '', c.dnc_reason || ''].join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'blocked_contacts.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Create campaign
  const handleCreateCampaign = async () => {
    if (!campaignData.name || !campaignData.agent_id || extractedContacts.length === 0) {
      return;
    }
    
    // Validate scheduled time if scheduling
    if (campaignData.start_mode === 'scheduled' && !campaignData.scheduled_at) {
      alert('Please select a scheduled date and time');
      return;
    }
    
    // Validate recurring schedule
    if (campaignData.start_mode === 'recurring') {
      if (!campaignData.recurrence_type || !campaignData.recurrence_time) {
        alert('Please select recurrence type and time');
        return;
      }
      if (campaignData.recurrence_type === 'weekly' && campaignData.recurrence_days.length === 0) {
        alert('Please select at least one day for weekly recurrence');
        return;
      }
      if (campaignData.recurrence_type === 'monthly' && campaignData.recurrence_days.length === 0) {
        alert('Please select at least one day of month for monthly recurrence');
        return;
      }
    }
    
    setCreating(true);
    try {
      const payload = {
        name: campaignData.name,
        description: campaignData.description,
        agent_id: campaignData.agent_id,
        from_number: campaignData.from_number,
        delay_between_calls: campaignData.delay_between_calls,
        max_concurrent_calls: campaignData.max_concurrent_calls,
        max_retries: campaignData.max_retries,
        save_recordings: campaignData.save_recordings,
        scheduled_at: campaignData.start_mode === 'scheduled' ? campaignData.scheduled_at : null,
        // Recurring options
        recurrence_type: campaignData.start_mode === 'recurring' ? campaignData.recurrence_type : null,
        recurrence_days: campaignData.start_mode === 'recurring' ? campaignData.recurrence_days : null,
        recurrence_time: campaignData.start_mode === 'recurring' ? campaignData.recurrence_time : null,
        recurrence_end_date: campaignData.start_mode === 'recurring' ? campaignData.recurrence_end_date : null,
        // Calling hours (timezone-aware)
        calling_hours: campaignData.calling_hours.enabled ? campaignData.calling_hours : null,
        contacts: extractedContacts
      };
      
      const response = await campaignsAPI.create(clientUser?.id, payload);
      
      if (response.data.success) {
        navigate('/client/campaigns');
      }
    } catch (err) {
      console.error('Error creating campaign:', err);
    } finally {
      setCreating(false);
    }
  };

  const getFileIcon = (filename) => {
    if (!filename) return <FileText className="w-8 h-8" />;
    const ext = filename.toLowerCase().split('.').pop();
    if (['xlsx', 'xls', 'csv'].includes(ext)) {
      return <FileSpreadsheet className="w-8 h-8 text-green-400" />;
    }
    return <File className="w-8 h-8 text-blue-400" />;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="create-campaign-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/client/campaigns')}
          className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">Create Campaign</h1>
          <p className="text-gray-400">Upload contacts and configure auto-dial settings</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-3">
        {[
          { num: 1, label: 'Upload' },
          { num: 2, label: 'Map Columns' },
          { num: 3, label: 'DNC Scrub' },
          { num: 4, label: 'Configure' }
        ].map((s, idx) => (
          <div key={s.num} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
              step >= s.num ? 'bg-teal-400 text-black' : 'bg-gray-800 text-gray-400'
            }`}>
              {step > s.num ? <Check className="w-4 h-4" /> : s.num}
            </div>
            <span className={`text-sm hidden sm:inline ${step >= s.num ? 'text-white' : 'text-gray-500'}`}>
              {s.label}
            </span>
            {idx < 3 && <div className={`w-8 h-0.5 ${step > s.num ? 'bg-teal-400' : 'bg-gray-800'}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: File Upload */}
      {step === 1 && (
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Upload Contact List</h2>
          <p className="text-gray-400 text-sm mb-6">
            Supported formats: CSV, Excel (.xlsx, .xls), Text (.txt), Word (.docx)
          </p>
          
          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              uploading ? 'border-teal-400 bg-teal-400/5' : 'border-gray-700 hover:border-gray-600'
            }`}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 text-teal-400 animate-spin" />
                <p className="text-gray-400">Uploading and parsing file...</p>
              </div>
            ) : uploadResult ? (
              <div className="flex flex-col items-center gap-3">
                {getFileIcon(file?.name)}
                <p className="text-white font-medium">{file?.name}</p>
                <p className="text-teal-400">{uploadResult.total_numbers_found} phone numbers found</p>
                <button
                  onClick={() => {
                    setFile(null);
                    setUploadResult(null);
                  }}
                  className="text-gray-400 hover:text-white text-sm"
                >
                  Upload different file
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <Upload className="w-10 h-10 text-gray-500" />
                <p className="text-gray-400">Drag and drop your file here, or</p>
                <label className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white cursor-pointer transition-colors">
                  Browse Files
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls,.txt,.docx"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              </div>
            )}
          </div>
          
          {uploadError && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <span className="text-red-400 text-sm">{uploadError}</span>
            </div>
          )}

          {/* Sample Data Preview */}
          {uploadResult?.sample_data?.length > 0 && uploadResult.headers?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-400 mb-2">Preview (first 5 rows)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800">
                      {uploadResult.headers.map((header, i) => (
                        <th key={`th-${header}-${i}`} className="px-3 py-2 text-left text-gray-400 font-medium">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResult.sample_data.map((row, i) => (
                      <tr key={`row-${i}`} className="border-b border-gray-800/50">
                        {row.map((cell, j) => (
                          <td key={`cell-${i}-${j}`} className="px-3 py-2 text-gray-300">
                            {String(cell).substring(0, 30)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setStep(2)}
              disabled={!uploadResult}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg text-black font-medium transition-colors"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Column Mapping */}
      {step === 2 && (
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Map Columns</h2>
          
          {/* Auto-extract option */}
          <div className="mb-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={useAutoExtract}
                onChange={(e) => setUseAutoExtract(e.target.checked)}
                className="w-4 h-4 rounded border-gray-600 text-teal-400 focus:ring-teal-400"
              />
              <span className="text-white">Use auto-detected phone numbers ({uploadResult?.total_numbers_found || 0} found)</span>
            </label>
          </div>
          
          {!useAutoExtract && uploadResult?.headers?.length > 0 && (
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-gray-300 text-sm mb-2">Phone Number Column *</label>
                <div className="relative">
                  <select
                    value={phoneColumn}
                    onChange={(e) => setPhoneColumn(e.target.value)}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white appearance-none pr-10"
                  >
                    <option value="">Select column</option>
                    {uploadResult.headers.map((header, i) => (
                      <option key={`phone-col-${header}-${i}`} value={header}>
                        {header} {uploadResult.detected_phone_columns?.includes(header) ? '(detected)' : ''}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              
              <div>
                <label className="block text-gray-300 text-sm mb-2">Name Column (optional)</label>
                <div className="relative">
                  <select
                    value={nameColumn}
                    onChange={(e) => setNameColumn(e.target.value)}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white appearance-none pr-10"
                  >
                    <option value="">None</option>
                    {uploadResult.headers.map((header, i) => (
                      <option key={`name-col-${header}-${i}`} value={header}>{header}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              
              <div>
                <label className="block text-gray-300 text-sm mb-2">Email Column (optional)</label>
                <div className="relative">
                  <select
                    value={emailColumn}
                    onChange={(e) => setEmailColumn(e.target.value)}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white appearance-none pr-10"
                  >
                    <option value="">None</option>
                    {uploadResult.headers.map((header, i) => (
                      <option key={`email-col-${header}-${i}`} value={header}>{header}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              
              <button
                onClick={handleExtractNumbers}
                disabled={!phoneColumn || extracting}
                className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded-lg text-white transition-colors"
              >
                {extracting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <Phone className="w-4 h-4" />
                    Extract Numbers
                  </>
                )}
              </button>
            </div>
          )}

          {/* Extracted contacts preview */}
          <div className="mt-4">
            <h3 className="text-sm font-medium text-gray-400 mb-2">
              Contacts to dial ({extractedContacts.length})
            </h3>
            <div className="max-h-48 overflow-y-auto bg-[#1a1a1f] rounded-lg border border-gray-700">
              {extractedContacts.slice(0, 20).map((contact, i) => (
                <div key={`contact-${contact.phone}-${i}`} className="px-3 py-2 border-b border-gray-800 last:border-0 flex items-center gap-3">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span className="text-white font-mono text-sm">{contact.phone}</span>
                  {contact.name && <span className="text-gray-400 text-sm">{contact.name}</span>}
                </div>
              ))}
              {extractedContacts.length > 20 && (
                <div className="px-3 py-2 text-gray-500 text-sm">
                  ... and {extractedContacts.length - 20} more
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-between">
            <button
              onClick={() => setStep(1)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={() => { handleScrubContacts(); setStep(3); }}
              disabled={extractedContacts.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg text-black font-medium transition-colors"
            >
              Next: Scrub DNC
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: DNC Scrub Results */}
      {step === 3 && (
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Shield className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">DNC List Scrub</h2>
              <p className="text-gray-400 text-sm">Checking contacts against your Do Not Call list</p>
            </div>
          </div>

          {scrubbing ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin mb-4" />
              <p className="text-gray-400">Scrubbing contacts against DNC list...</p>
            </div>
          ) : scrubResult ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-[#1a1a1f] border border-gray-700 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                    <FileText className="w-4 h-4" />
                    Total Uploaded
                  </div>
                  <p className="text-2xl font-bold text-white">{scrubResult.summary?.total || 0}</p>
                </div>
                <div className="bg-[#1a1a1f] border border-green-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-green-400 text-sm mb-1">
                    <CheckCircle className="w-4 h-4" />
                    Clean Contacts
                  </div>
                  <p className="text-2xl font-bold text-green-400">{scrubResult.summary?.clean || 0}</p>
                </div>
                <div className="bg-[#1a1a1f] border border-orange-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-orange-400 text-sm mb-1">
                    <Ban className="w-4 h-4" />
                    DNC Blocked
                  </div>
                  <p className="text-2xl font-bold text-orange-400">{scrubResult.summary?.blocked || 0}</p>
                </div>
              </div>

              {/* Blocked Contacts Section */}
              {scrubResult.blocked_contacts?.length > 0 && (
                <div className="bg-[#1a1a1f] border border-orange-500/20 rounded-xl overflow-hidden">
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#252530]"
                    onClick={() => setShowBlockedList(!showBlockedList)}
                  >
                    <div className="flex items-center gap-2">
                      <Ban className="w-5 h-5 text-orange-400" />
                      <span className="text-white font-medium">
                        {scrubResult.blocked_contacts.length} contacts will be skipped (on DNC list)
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); downloadBlockedContacts(); }}
                        className="flex items-center gap-1 px-3 py-1 text-sm text-orange-400 hover:bg-orange-500/10 rounded-lg transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showBlockedList ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                  
                  {showBlockedList && (
                    <div className="border-t border-gray-800 max-h-48 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-900/50 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-gray-400 font-medium">Phone</th>
                            <th className="px-4 py-2 text-left text-gray-400 font-medium">Name</th>
                            <th className="px-4 py-2 text-left text-gray-400 font-medium">DNC Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scrubResult.blocked_contacts.map((contact, i) => (
                            <tr key={`blocked-${contact.phone}-${i}`} className="border-t border-gray-800/50">
                              <td className="px-4 py-2 text-white font-mono">{contact.phone}</td>
                              <td className="px-4 py-2 text-gray-300">{contact.name || '-'}</td>
                              <td className="px-4 py-2 text-orange-400 text-xs">{contact.dnc_reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Clean Contacts Summary */}
              <div className="bg-[#1a1a1f] border border-green-500/20 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-white font-medium">
                    {scrubResult.clean_contacts?.length || 0} contacts ready to dial
                  </span>
                </div>
                <p className="text-gray-400 text-sm">
                  These contacts are not on your DNC list and will be included in the campaign.
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
              <p className="text-gray-400">Scrub results will appear here</p>
            </div>
          )}

          <div className="mt-6 flex justify-between">
            <button
              onClick={() => setStep(2)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={() => setStep(4)}
              disabled={scrubbing || !scrubResult || extractedContacts.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg text-black font-medium transition-colors"
            >
              Continue with {extractedContacts.length} contacts
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Campaign Settings */}
      {step === 4 && (
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Campaign Settings</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-gray-300 text-sm mb-2">Campaign Name *</label>
              <input
                type="text"
                value={campaignData.name}
                onChange={(e) => setCampaignData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                placeholder="My Auto-Dial Campaign"
              />
            </div>
            
            <div>
              <label className="block text-gray-300 text-sm mb-2">Description</label>
              <textarea
                value={campaignData.description}
                onChange={(e) => setCampaignData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                placeholder="Campaign description..."
                rows={2}
              />
            </div>
            
            <div>
              <label className="block text-gray-300 text-sm mb-2">AI Agent *</label>
              <div className="relative">
                <select
                  value={campaignData.agent_id}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, agent_id: e.target.value }))}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white appearance-none pr-10"
                >
                  <option value="">Select agent</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>{agent.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
            
            <div>
              <label className="block text-gray-300 text-sm mb-2">Caller ID (From Number)</label>
              <div className="relative">
                <select
                  value={campaignData.from_number}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, from_number: e.target.value }))}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white appearance-none pr-10"
                >
                  {phoneNumbers.map((num) => (
                    <option key={num.number} value={num.number}>{num.label}: {num.number}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">Delay Between Calls (seconds)</label>
                <input
                  type="number"
                  min={5}
                  max={300}
                  value={campaignData.delay_between_calls}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, delay_between_calls: parseInt(e.target.value) || 30 }))}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                />
              </div>
              
              <div>
                <label className="block text-gray-300 text-sm mb-2">Max Concurrent Calls</label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={campaignData.max_concurrent_calls}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, max_concurrent_calls: parseInt(e.target.value) || 1 }))}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">Retry Attempts (for failed calls)</label>
                <input
                  type="number"
                  min={0}
                  max={5}
                  value={campaignData.max_retries}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, max_retries: parseInt(e.target.value) || 0 }))}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                />
              </div>
              
              <div className="flex items-center pt-7">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={campaignData.save_recordings}
                    onChange={(e) => setCampaignData(prev => ({ ...prev, save_recordings: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-600 text-teal-400 focus:ring-teal-400"
                  />
                  <span className="text-gray-300 text-sm">Save call recordings</span>
                </label>
              </div>
            </div>

            {/* Scheduling Options */}
            <div className="mt-4 p-4 bg-[#1a1a1f] border border-gray-700 rounded-lg">
              <h3 className="text-white font-medium mb-4">When to Start</h3>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="start_mode"
                    value="now"
                    checked={campaignData.start_mode === 'now'}
                    onChange={() => setCampaignData(prev => ({ ...prev, start_mode: 'now', scheduled_at: null }))}
                    className="w-4 h-4 border-gray-600 text-teal-400 focus:ring-teal-400"
                  />
                  <span className="text-gray-300">Create as Draft (start manually)</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="start_mode"
                    value="scheduled"
                    checked={campaignData.start_mode === 'scheduled'}
                    onChange={() => setCampaignData(prev => ({ ...prev, start_mode: 'scheduled' }))}
                    className="w-4 h-4 border-gray-600 text-teal-400 focus:ring-teal-400"
                  />
                  <span className="text-gray-300">Schedule for later</span>
                </label>
                
                {campaignData.start_mode === 'scheduled' && (
                  <div className="ml-7 mt-3 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Date</label>
                      <input
                        type="date"
                        value={campaignData.scheduled_at ? campaignData.scheduled_at.split('T')[0] : ''}
                        onChange={(e) => {
                          const date = e.target.value;
                          const time = campaignData.scheduled_at ? campaignData.scheduled_at.split('T')[1]?.substring(0, 5) : '09:00';
                          setCampaignData(prev => ({ ...prev, scheduled_at: `${date}T${time}:00Z` }));
                        }}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full bg-[#0d1117] border border-gray-700 rounded-lg px-3 py-2 text-white outline-none focus:border-teal-400"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Time (UTC)</label>
                      <input
                        type="time"
                        value={campaignData.scheduled_at ? campaignData.scheduled_at.split('T')[1]?.substring(0, 5) : '09:00'}
                        onChange={(e) => {
                          const time = e.target.value;
                          const date = campaignData.scheduled_at ? campaignData.scheduled_at.split('T')[0] : new Date().toISOString().split('T')[0];
                          setCampaignData(prev => ({ ...prev, scheduled_at: `${date}T${time}:00Z` }));
                        }}
                        className="w-full bg-[#0d1117] border border-gray-700 rounded-lg px-3 py-2 text-white outline-none focus:border-teal-400"
                      />
                    </div>
                  </div>
                )}
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="start_mode"
                    value="recurring"
                    checked={campaignData.start_mode === 'recurring'}
                    onChange={() => setCampaignData(prev => ({ 
                      ...prev, 
                      start_mode: 'recurring',
                      scheduled_at: null,
                      recurrence_type: 'weekly',
                      recurrence_days: [0] // Default to Monday
                    }))}
                    className="w-4 h-4 border-gray-600 text-teal-400 focus:ring-teal-400"
                  />
                  <span className="text-gray-300">Set up recurring schedule</span>
                </label>
                
                {campaignData.start_mode === 'recurring' && (
                  <div className="ml-7 mt-3 space-y-4 p-4 bg-[#0d1117] rounded-lg border border-gray-700">
                    {/* Recurrence Type */}
                    <div>
                      <label className="block text-gray-400 text-xs mb-2">Repeat</label>
                      <div className="flex gap-2">
                        {['daily', 'weekly', 'monthly'].map((type) => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => setCampaignData(prev => ({ 
                              ...prev, 
                              recurrence_type: type,
                              recurrence_days: type === 'weekly' ? [0] : type === 'monthly' ? [1] : []
                            }))}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                              campaignData.recurrence_type === type
                                ? 'bg-teal-400/20 text-teal-400 border border-teal-400/30'
                                : 'bg-gray-800 text-gray-400 hover:text-white border border-gray-700'
                            }`}
                          >
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Weekly Day Selection */}
                    {campaignData.recurrence_type === 'weekly' && (
                      <div>
                        <label className="block text-gray-400 text-xs mb-2">On days</label>
                        <div className="flex flex-wrap gap-2">
                          {dayNames.map((day, i) => (
                            <button
                              key={day}
                              type="button"
                              onClick={() => {
                                const days = campaignData.recurrence_days.includes(i)
                                  ? campaignData.recurrence_days.filter(d => d !== i)
                                  : [...campaignData.recurrence_days, i];
                                setCampaignData(prev => ({ ...prev, recurrence_days: days }));
                              }}
                              className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                                campaignData.recurrence_days.includes(i)
                                  ? 'bg-teal-400 text-black'
                                  : 'bg-gray-800 text-gray-400 hover:text-white border border-gray-700'
                              }`}
                            >
                              {day}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Monthly Day Selection */}
                    {campaignData.recurrence_type === 'monthly' && (
                      <div>
                        <label className="block text-gray-400 text-xs mb-2">On day(s) of month</label>
                        <div className="grid grid-cols-7 gap-1">
                          {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                            <button
                              key={day}
                              type="button"
                              onClick={() => {
                                const days = campaignData.recurrence_days.includes(day)
                                  ? campaignData.recurrence_days.filter(d => d !== day)
                                  : [...campaignData.recurrence_days, day];
                                setCampaignData(prev => ({ ...prev, recurrence_days: days }));
                              }}
                              className={`w-8 h-8 rounded text-xs font-medium transition-colors ${
                                campaignData.recurrence_days.includes(day)
                                  ? 'bg-teal-400 text-black'
                                  : 'bg-gray-800 text-gray-400 hover:text-white'
                              }`}
                            >
                              {day}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Time Selection */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-400 text-xs mb-1">Time (UTC)</label>
                        <input
                          type="time"
                          value={campaignData.recurrence_time}
                          onChange={(e) => setCampaignData(prev => ({ ...prev, recurrence_time: e.target.value }))}
                          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white outline-none focus:border-teal-400"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-xs mb-1">End Date (Optional)</label>
                        <input
                          type="date"
                          value={campaignData.recurrence_end_date || ''}
                          onChange={(e) => setCampaignData(prev => ({ ...prev, recurrence_end_date: e.target.value || null }))}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white outline-none focus:border-teal-400"
                        />
                      </div>
                    </div>
                    
                    {/* Preview */}
                    <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                      <p className="text-purple-400 text-sm">
                        {campaignData.recurrence_type === 'daily' && (
                          <>Runs daily at {campaignData.recurrence_time} UTC</>
                        )}
                        {campaignData.recurrence_type === 'weekly' && (
                          <>Runs every {campaignData.recurrence_days.sort((a,b)=>a-b).map(d => dayNames[d]).join(', ')} at {campaignData.recurrence_time} UTC</>
                        )}
                        {campaignData.recurrence_type === 'monthly' && (
                          <>Runs on day(s) {campaignData.recurrence_days.sort((a,b)=>a-b).join(', ')} of each month at {campaignData.recurrence_time} UTC</>
                        )}
                        {campaignData.recurrence_end_date && (
                          <> (until {campaignData.recurrence_end_date})</>
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Calling Hours (Timezone-Aware) */}
            <div className="mt-4 p-4 bg-[#1a1a1f] border border-gray-700 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-400" />
                  <h3 className="text-white font-medium">Calling Hours</h3>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={campaignData.calling_hours.enabled}
                    onChange={(e) => setCampaignData(prev => ({
                      ...prev,
                      calling_hours: { ...prev.calling_hours, enabled: e.target.checked }
                    }))}
                    className="w-4 h-4 rounded border-gray-600 text-blue-400 focus:ring-blue-400"
                  />
                  <span className="text-gray-300 text-sm">Enable timezone-aware calling</span>
                </label>
              </div>
              
              <p className="text-gray-500 text-sm mb-4">
                Only dial contacts during appropriate business hours based on their timezone (inferred from area code).
              </p>

              {campaignData.calling_hours.enabled && (
                <div className="space-y-4 border-t border-gray-700 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Start Time (Local)</label>
                      <input
                        type="time"
                        value={campaignData.calling_hours.start_time}
                        onChange={(e) => setCampaignData(prev => ({
                          ...prev,
                          calling_hours: { ...prev.calling_hours, start_time: e.target.value }
                        }))}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white outline-none focus:border-blue-400"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">End Time (Local)</label>
                      <input
                        type="time"
                        value={campaignData.calling_hours.end_time}
                        onChange={(e) => setCampaignData(prev => ({
                          ...prev,
                          calling_hours: { ...prev.calling_hours, end_time: e.target.value }
                        }))}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white outline-none focus:border-blue-400"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-xs mb-2">Calling Days</label>
                    <div className="flex gap-2">
                      {dayNames.map((name, idx) => (
                        <button
                          key={name}
                          type="button"
                          onClick={() => {
                            const days = campaignData.calling_hours.days_of_week.includes(idx)
                              ? campaignData.calling_hours.days_of_week.filter(d => d !== idx)
                              : [...campaignData.calling_hours.days_of_week, idx];
                            setCampaignData(prev => ({
                              ...prev,
                              calling_hours: { ...prev.calling_hours, days_of_week: days }
                            }));
                          }}
                          className={`w-10 h-8 rounded text-xs font-medium transition-colors ${
                            campaignData.calling_hours.days_of_week.includes(idx)
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-800 text-gray-400 hover:text-white'
                          }`}
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Default Timezone (for contacts without area code)</label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        value={campaignData.calling_hours.default_timezone}
                        onChange={(e) => setCampaignData(prev => ({
                          ...prev,
                          calling_hours: { ...prev.calling_hours, default_timezone: e.target.value }
                        }))}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white appearance-none"
                      >
                        {timezones.map(tz => (
                          <option key={tz.id} value={tz.id}>{tz.name}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-blue-400 text-sm">
                      Contacts will only be called between <strong>{campaignData.calling_hours.start_time}</strong> and <strong>{campaignData.calling_hours.end_time}</strong> (their local time) on{' '}
                      {campaignData.calling_hours.days_of_week.length === 7 
                        ? 'all days'
                        : campaignData.calling_hours.days_of_week.sort((a,b)=>a-b).map(d => dayNames[d]).join(', ')
                      }.
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Summary */}
            <div className="mt-4 p-4 bg-teal-500/10 border border-teal-500/30 rounded-lg">
              <h3 className="text-teal-400 font-medium mb-2">Campaign Summary</h3>
              <ul className="text-gray-300 text-sm space-y-1">
                <li>• {extractedContacts.length} contacts will be dialed</li>
                <li>• AI Agent: {agents.find(a => a.id === campaignData.agent_id)?.name || 'Not selected'}</li>
                <li>• {campaignData.delay_between_calls}s delay between calls</li>
                <li>• Up to {campaignData.max_concurrent_calls} concurrent call(s)</li>
                <li>• {campaignData.max_retries} retry attempt(s) for failed calls</li>
                <li>• Recordings: {campaignData.save_recordings ? 'Enabled' : 'Disabled'}</li>
                {campaignData.calling_hours.enabled && (
                  <li className="text-blue-400">
                    • Calling hours: {campaignData.calling_hours.start_time} - {campaignData.calling_hours.end_time} (contact&apos;s local time)
                  </li>
                )}
                {campaignData.start_mode === 'scheduled' && campaignData.scheduled_at && (
                  <li className="text-teal-400">• Scheduled: {new Date(campaignData.scheduled_at).toLocaleString()} UTC</li>
                )}
                {campaignData.start_mode === 'recurring' && campaignData.recurrence_type && (
                  <li className="text-purple-400">
                    • Recurring: {campaignData.recurrence_type === 'daily' ? 'Daily' : 
                      campaignData.recurrence_type === 'weekly' ? `Weekly on ${campaignData.recurrence_days.map(d => dayNames[d]).join(', ')}` :
                      `Monthly on day ${campaignData.recurrence_days.join(', ')}`} at {campaignData.recurrence_time} UTC
                  </li>
                )}
              </ul>
            </div>
          </div>

          <div className="mt-6 flex justify-between">
            <button
              onClick={() => setStep(3)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={handleCreateCampaign}
              disabled={!campaignData.name || !campaignData.agent_id || creating}
              className="flex items-center gap-2 px-6 py-2.5 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg text-black font-medium transition-colors"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Create Campaign
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateCampaignPage;
